import {
  cursorPaging,
  flattenPages,
  type CommsLogEntry,
  type CommsLogFilters,
  type CommsMessage,
  type CommsMessageFilters,
  type CommsMessageInput,
  type CommsMessagePatch,
  type CommsRegion,
  type CommsTextThread,
  type CommsUnknownCaller,
} from "@clubcore/api";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useScope } from "./scope";

/** After any change, every communications list and detail is read again. */
function useRefresh() {
  const { queryClient, slug } = useScope();
  return () => queryClient.invalidateQueries({ queryKey: [slug, "comms"] });
}

// --- the log -----------------------------------------------------------------------------------

export function useLogEntries(filter: CommsLogFilters) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "log", filter],
    placeholderData: keepPreviousData,
    ...cursorPaging<CommsLogEntry>(({ cursor, signal }) => api.communications.listLog({ cursor, signal, limit: 50, ...filter })),
  });
}

export function useLogEntry(id: string) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "comms", "log-entry", id], queryFn: ({ signal }) => api.communications.getLogEntry(id, signal) });
}

/** `memberId: null` takes the link away. */
export function useLinkLogEntry() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { entryId: string; memberId: string | null }) => api.communications.linkLogEntry(input.entryId, { member_id: input.memberId }),
    onSuccess: refresh,
  });
}

/** A member's calls and texts, a page of `pageSize` at a time. */
export function useMemberTimeline(memberId: string, pageSize = 10) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "timeline", memberId, pageSize],
    ...cursorPaging<CommsLogEntry>(({ cursor, signal }) => api.communications.memberTimeline(memberId, { cursor, signal, limit: pageSize })),
  });
}

// --- unknown callers ---------------------------------------------------------------------------

export function useUnknownCallers() {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "unknown"],
    ...cursorPaging<CommsUnknownCaller>(({ cursor, signal }) => api.communications.listUnknown({ cursor, signal, limit: 50 })),
  });
}

export function useLinkUnknown() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { counterpart: string; memberId: string; addContact: boolean }) =>
      api.communications.linkUnknown({ counterpart: input.counterpart, member_id: input.memberId, add_contact: input.addContact }),
    onSuccess: refresh,
  });
}

// --- phone messages ----------------------------------------------------------------------------

export function useMessages(filter: CommsMessageFilters, enabled: boolean) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "messages", "all", filter],
    enabled,
    placeholderData: keepPreviousData,
    ...cursorPaging<CommsMessage>(({ cursor, signal }) => api.communications.listMessages({ cursor, signal, limit: 50, ...filter })),
  });
}

export function useMyMessages(state: "open" | "done" | undefined, enabled: boolean) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "messages", "mine", state ?? "any"],
    enabled,
    placeholderData: keepPreviousData,
    ...cursorPaging<CommsMessage>(({ cursor, signal }) => api.communications.listMyMessages({ cursor, signal, limit: 50, state })),
  });
}

export function useRecipients(enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "comms", "recipients"], enabled, queryFn: ({ signal }) => api.communications.listRecipients(signal) });
}

export function useCreateMessage() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({ mutationFn: (input: CommsMessageInput) => api.communications.createMessage(input), onSuccess: refresh });
}

export function useUpdateMessage() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { id: string; patch: CommsMessagePatch }) => api.communications.updateMessage(input.id, input.patch),
    onSuccess: refresh,
  });
}

export function useDeleteMessage() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({ mutationFn: (id: string) => api.communications.deleteMessage(id), onSuccess: refresh });
}

// --- calling and texting -----------------------------------------------------------------------

/**
 * What a phone number may offer this user. Asked once per club (and again after five minutes), only by someone who could use it
 * (`enabled`: holds comms.write and the module is not blocked), so screens for anyone else cost nothing.
 */
export function useCapabilities(enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "comms", "capabilities"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => api.communications.capabilities(signal),
  });
}

export function usePlaceCall() {
  const { api } = useScope();
  return useMutation({ mutationFn: (input: { number: string; memberId?: string | undefined }) =>
    api.communications.placeCall({ number: input.number, ...(input.memberId ? { member_id: input.memberId } : {}) }) });
}

export function useSendText() {
  const { api } = useScope();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { number: string; text: string; key: string; memberId?: string | undefined }) =>
      api.communications.sendText({ number: input.number, text: input.text, idempotency_key: input.key, ...(input.memberId ? { member_id: input.memberId } : {}) }),
    onSettled: refresh,
  });
}

export function useConversations(unreadOnly: boolean) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "conversations", unreadOnly],
    placeholderData: keepPreviousData,
    ...cursorPaging<CommsTextThread>(({ cursor, signal }) => api.communications.listConversations({ cursor, signal, limit: 50, unreadOnly: unreadOnly || undefined })),
  });
}

/** One conversation, newest page first. The header (who it is, opted out) comes with the first page. */
export function useConversation(number: string, enabled = true) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "comms", "conversation", number],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.communications.getConversation(number, { cursor: pageParam, signal, limit: 30 }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

export function useMarkConversationRead() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (number: string) => api.communications.markConversationRead(number),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [slug, "comms", "conversations"] });
      void queryClient.invalidateQueries({ queryKey: [slug, "comms", "texts-unread"] });
    },
  });
}

/** Unread texts for the menu badge, read again every minute (and at once when a text arrives on an open screen). */
export function useUnreadTexts(enabled = true) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "comms", "texts-unread"],
    enabled,
    refetchInterval: 60_000,
    queryFn: ({ signal }) => api.communications.unreadTexts(signal),
  });
}

// --- settings ----------------------------------------------------------------------------------

export function useCommsSettings() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "comms", "settings"], queryFn: ({ signal }) => api.communications.getSettings(signal) });
}

export function useUpdateCommsSettings() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (region: CommsRegion) => api.communications.updateSettings({ default_region: region }),
    onSuccess: (data) => queryClient.setQueryData([slug, "comms", "settings"], data),
  });
}

/** The text sent back to HELP; blank or null returns to the default. */
export function useUpdateHelpReply() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (reply: string | null) => api.communications.updateSettings({ sms_help_reply: reply }),
    onSuccess: (data) => queryClient.setQueryData([slug, "comms", "settings"], data),
  });
}

/** Members matching what was typed, for the member picker. Needs `members.read`. */
export function useMemberLookup(text: string) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "comms", "member-lookup", text],
    enabled: text.length >= 2,
    queryFn: async ({ signal }) => flattenPages({ pages: [await api.members.listMembers({ q: text, limit: 8, lookup: true, signal })] }),
  });
}
